import type { UpdateOrganizationInput } from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { sessionKeys } from '../session/keys.ts'
import { orgKeys } from './keys.ts'

/**
 * The active organization's full profile (`org:read` — settings screens).
 * Shells that only need name/slug should use `useOrg()` instead, which works
 * for every role from the `/me` payload.
 */
export function useOrganizationProfile() {
  const api = useApi()
  return useQuery({
    queryKey: orgKeys.profile(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.organization.get()
      if (error) throw error
      return data
    },
  })
}

export function useUpdateOrganization() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateOrganizationInput) => {
      const { data, error } = await api.api.v1.organization.patch(input)
      if (error) throw error
      return data
    },
    onSuccess: () => {
      // The org name/logo appear in both the profile and the shell's org
      // switcher (which reads /me).
      void queryClient.invalidateQueries({ queryKey: orgKeys.profile() })
      void queryClient.invalidateQueries({ queryKey: sessionKeys.me() })
    },
  })
}
